/**
 * Every URL Disciplio analytics touches must be pathname-only: no query strings
 * (reset-password tokens, IDs) and no hashes ever leave the browser.
 */
export function sanitizePathname(pathname: string): string {
  const withoutQuery = pathname.split("?")[0] ?? pathname;
  return withoutQuery.split("#")[0] ?? withoutQuery;
}

const URL_LIKE_PROPERTY_KEYS = new Set([
  "$current_url",
  "$pathname",
  "$referrer",
  "$referring_domain",
  "$prev_pageview_pathname",
  "$session_entry_url",
  "$session_entry_pathname",
  "$session_entry_referrer",
  "$session_entry_referring_domain",
  "$initial_current_url",
  "$initial_pathname",
  "$initial_referrer",
  "$initial_referring_domain",
  "path",
  "url",
]);

const URL_LIKE_KEY_SUFFIX = /(_url|_pathname|_referrer|_referring_domain)$/;

function isUrlLikeKey(key: string): boolean {
  return URL_LIKE_PROPERTY_KEYS.has(key) || URL_LIKE_KEY_SUFFIX.test(key);
}

/**
 * PostHog attaches page-URL properties ($current_url, $pathname, ...) to most
 * captured events, including pageviews, pageleave, and session-recording
 * lifecycle events. This strips query strings and hashes from any such
 * property so tokens or other sensitive query params never reach PostHog,
 * regardless of which capture path produced the event.
 */
export function stripUrlPropertiesInPlace(
  properties: Record<string, unknown>
): Record<string, unknown> {
  for (const key of Object.keys(properties)) {
    if (!isUrlLikeKey(key)) continue;
    const value = properties[key];
    if (typeof value !== "string") continue;
    try {
      const url = new URL(value, "https://placeholder.invalid");
      properties[key] =
        `${url.origin === "https://placeholder.invalid" ? "" : url.origin}${url.pathname}`;
    } catch {
      properties[key] = sanitizePathname(value);
    }
  }
  return properties;
}
